/**
 * Motoboys: quem entrega e quanto cada um tem a receber.
 *
 * O ganho é a TAXA DE ENTREGA do pedido que a pessoa levou. Como a taxa sai
 * da distância, quem pega corrida longa ganha mais — e isso aparece aqui sem
 * ninguém calcular nada.
 */
import { formatarReais } from '@du/shared'
import {
  Badge,
  Button,
  EmptyState,
  Icone,
  Input,
  Modal,
  SectionTitle,
  Skeleton,
  StatTile,
  useAviso,
} from '@du/ui'
import { Bike, Pencil, Plus, TriangleAlert } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api.js'

type Motoboy = {
  id: string
  nome: string
  telefone: string | null
  chavePix: string | null
  ativo: boolean
}

type Periodo = { entregas: number; ganhoCents: number }

type Ganhos = {
  periodo: { de: string; ate: string; dias: number }
  motoboys: (Motoboy & {
    entregas: number
    ganhoCents: number
    naoConcluidas: number
    resumo: { hoje: Periodo; semana: Periodo; mes: Periodo }
    serie: { dia: string; entregas: number; ganhoCents: number }[]
  })[]
  semMotoboy: number
}

/** "1 entrega", "2 entregas". Texto que erra plural parece sistema descuidado. */
const plural = (n: number, singular: string, plural_: string) =>
  `${n} ${n === 1 ? singular : plural_}`

const hoje = () => new Date().toISOString().slice(0, 10)
const somarDias = (dia: string, n: number) => {
  const [a, m, d] = dia.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(a, m - 1, d) + n * 86_400_000).toISOString().slice(0, 10)
}

export function Motoboys() {
  const [dados, setDados] = useState<Ganhos | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [de, setDe] = useState(somarDias(hoje(), -29))
  const [ate, setAte] = useState(hoje())
  const [editando, setEditando] = useState<Motoboy | null>(null)
  const [novo, setNovo] = useState(false)
  const { avisar } = useAviso()

  const carregar = useCallback(async () => {
    setCarregando(true)
    try {
      setDados(await api<Ganhos>(`/v1/motoboys/ganhos?de=${de}&ate=${ate}`))
    } catch (e) {
      avisar({ tom: 'erro', texto: e instanceof ErroApi ? e.message : 'Não deu para carregar.' })
    } finally {
      setCarregando(false)
    }
  }, [de, ate, avisar])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const maiorGanho = Math.max(1, ...(dados?.motoboys ?? []).map((m) => m.ganhoCents))

  return (
    <div className="pagina">
      <SectionTitle
        tamanho="sm"
        acao={
          <Button tamanho="sm" icone={Plus} onClick={() => setNovo(true)}>
            Novo motoboy
          </Button>
        }
      >
        Motoboys
      </SectionTitle>

      <div className="dash-filtros">
        <Button variante="ghost" tamanho="sm" onClick={() => { setAte(hoje()); setDe(hoje()) }}>
          Hoje
        </Button>
        <Button variante="ghost" tamanho="sm" onClick={() => { setAte(hoje()); setDe(somarDias(hoje(), -6)) }}>
          7 dias
        </Button>
        <Button variante="ghost" tamanho="sm" onClick={() => { setAte(hoje()); setDe(somarDias(hoje(), -29)) }}>
          30 dias
        </Button>
        <Input rotulo="De" type="date" value={de} max={ate} onChange={(e) => setDe(e.target.value)} />
        <Input rotulo="Até" type="date" value={ate} max={hoje()} min={de} onChange={(e) => setAte(e.target.value)} />
      </div>

      {carregando ? (
        <Skeleton altura={320} />
      ) : !dados || dados.motoboys.length === 0 ? (
        <EmptyState
          icone={Bike}
          titulo="Nenhum motoboy cadastrado"
          texto="Cadastre quem entrega para acompanhar quanto cada um tem a receber."
          acao={<Button icone={Plus} onClick={() => setNovo(true)}>Cadastrar o primeiro</Button>}
        />
      ) : (
        <>
          {/*
            Entregas sem motoboy atribuído. Fica no topo porque é o que falta
            registrar — e, sem alguém olhar, some da conta de todo mundo.
          */}
          {dados.semMotoboy > 0 ? (
            <div className="dash-aviso" role="status">
              <Icone de={TriangleAlert} tamanho={18} />{' '}
              <strong>{dados.semMotoboy}</strong>{' '}
              {dados.semMotoboy === 1 ? 'entrega concluída não tem' : 'entregas concluídas não têm'}{' '}
              motoboy atribuído no período. Esse valor não entrou na conta de ninguém.
            </div>
          ) : null}

          {dados.motoboys.map((m) => (
            <section key={m.id} className="mb-cartao">
              <header className="mb-cartao__topo">
                <div>
                  <h3 className="mb-cartao__nome">
                    {m.nome}
                    {!m.ativo ? <Badge tom="neutro">inativo</Badge> : null}
                  </h3>
                  {m.telefone ? (
                    <a className="mb-cartao__tel" href={`tel:${m.telefone}`}>
                      {m.telefone}
                    </a>
                  ) : null}
                </div>
                <Button
                  variante="ghost"
                  tamanho="sm"
                  icone={Pencil}
                  onClick={() => setEditando(m)}
                  aria-label={`Editar ${m.nome}`}
                >
                  Editar
                </Button>
              </header>

              <div className="grade-stats">
                <StatTile rotulo="Hoje" valor={formatarReais(m.resumo.hoje.ganhoCents)} tom="marca"
                  apoio={plural(m.resumo.hoje.entregas, 'entrega', 'entregas')} />
                <StatTile rotulo="Esta semana" valor={formatarReais(m.resumo.semana.ganhoCents)}
                  apoio={`${plural(m.resumo.semana.entregas, 'entrega', 'entregas')} · desde segunda`} />
                <StatTile rotulo="Este mês" valor={formatarReais(m.resumo.mes.ganhoCents)}
                  apoio={plural(m.resumo.mes.entregas, 'entrega', 'entregas')} />
                <StatTile rotulo="No período escolhido" valor={formatarReais(m.ganhoCents)}
                  apoio={`${plural(m.entregas, 'entrega', 'entregas')} em ${plural(dados.periodo.dias, 'dia', 'dias')}`} />
              </div>

              {/* Barra comparativa: quem está levando mais, de relance. */}
              <span className="mb-barra">
                <span
                  className="mb-barra__preenchimento"
                  style={{ width: `${Math.max(2, (m.ganhoCents / maiorGanho) * 100)}%` }}
                />
              </span>

              <div className="mb-cartao__pe">
                {m.chavePix ? (
                  <span className="mb-cartao__pix">
                    Pix para acerto: <code>{m.chavePix}</code>
                  </span>
                ) : (
                  <span className="mb-cartao__pix mb-cartao__pix--falta">
                    Sem chave Pix cadastrada
                  </span>
                )}
                {m.naoConcluidas > 0 ? (
                  <span className="mb-cartao__perdidas">
                    {plural(m.naoConcluidas, 'viagem perdida', 'viagens perdidas')}
                  </span>
                ) : null}
              </div>
            </section>
          ))}
        </>
      )}

      {novo || editando ? (
        <FormMotoboy
          motoboy={editando}
          aoFechar={() => {
            setNovo(false)
            setEditando(null)
          }}
          aoSalvar={() => {
            setNovo(false)
            setEditando(null)
            void carregar()
          }}
        />
      ) : null}
    </div>
  )
}

function FormMotoboy({
  motoboy,
  aoFechar,
  aoSalvar,
}: {
  motoboy: Motoboy | null
  aoFechar: () => void
  aoSalvar: () => void
}) {
  const [nome, setNome] = useState(motoboy?.nome ?? '')
  const [telefone, setTelefone] = useState(motoboy?.telefone ?? '')
  const [chavePix, setChavePix] = useState(motoboy?.chavePix ?? '')
  const [ativo, setAtivo] = useState(motoboy?.ativo ?? true)
  const [salvando, setSalvando] = useState(false)
  const { avisar } = useAviso()

  const salvar = async () => {
    if (nome.trim().length < 2) return
    setSalvando(true)
    try {
      const corpo = {
        nome: nome.trim(),
        telefone: telefone.trim() || null,
        chavePix: chavePix.trim() || null,
        ativo,
      }
      if (motoboy) {
        await api(`/v1/motoboys/${motoboy.id}`, { method: 'PATCH', corpo })
      } else {
        await api('/v1/motoboys', { method: 'POST', corpo })
      }
      aoSalvar()
    } catch (e) {
      avisar({ tom: 'erro', texto: e instanceof ErroApi ? e.message : 'Não deu para salvar.' })
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Modal
      aberto
      titulo={motoboy ? `Editar ${motoboy.nome}` : 'Novo motoboy'}
      aoFechar={aoFechar}
      rodape={
        <>
          <Button variante="ghost" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button onClick={() => void salvar()} carregando={salvando}>
            Salvar
          </Button>
        </>
      }
    >
      <Input rotulo="Nome" value={nome} onChange={(e) => setNome(e.target.value)} required autoFocus />
      <Input
        rotulo="WhatsApp"
        value={telefone}
        onChange={(e) => setTelefone(e.target.value)}
        placeholder="(11) 99999-0000"
        dica="Para ligar com um toque do portal"
      />
      <Input
        rotulo="Chave Pix"
        value={chavePix}
        onChange={(e) => setChavePix(e.target.value)}
        dica="Só para o acerto; o sistema não paga ninguém automaticamente"
      />
      {motoboy ? (
        <label className="mb-ativo">
          <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
          <span>
            Ativo
            <small>Inativo some do seletor no Kanban, mas o histórico continua.</small>
          </span>
        </label>
      ) : null}
    </Modal>
  )
}
